import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { ClosedDayResponse } from "../contracts/ClosedDayResponse";
import { MarkDayClosedRequest } from "../contracts/MarkDayClosedRequest";
import { MarkDayClosedResponse } from "../contracts/MarkDayClosedResponse";
import { ClosedDay } from "../models/ClosedDay";
import { ClosedDayResponseMapper } from "../mappers/ClosedDayResponseMapper";

const mapper = new ClosedDayResponseMapper();

/** Loads the instructor's closed days within the inclusive [from, to] range. */
export const fetchClosedDays = createAsyncThunk<
  ClosedDay[],
  { from: string; to: string },
  { rejectValue: ApiErrorResponse }
>("closedDays/fetch", async ({ from, to }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ClosedDayResponse[]>("/closed-days", {
      params: { from, to },
    });
    return mapper.toClosedDays(response.data);
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to load closed days"));
  }
});

/** Marks a day closed, optionally cancelling that day's occurrences. */
export const markDayClosed = createAsyncThunk<
  MarkDayClosedResponse,
  MarkDayClosedRequest,
  { rejectValue: ApiErrorResponse }
>("closedDays/mark", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MarkDayClosedResponse>("/closed-days", request);
    return response.data;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to close the day"));
  }
});

/** Reopens a day by removing its marker. Returns the removed marker's id. */
export const reopenDay = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("closedDays/reopen", async (closedDayId, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`/closed-days/${closedDayId}`);
    return closedDayId;
  } catch (error: unknown) {
    return rejectWithValue(toApiError(error, "Failed to reopen the day"));
  }
});
